import * as THREE from "three";

import type {
  LoomAirfoil,
  LoomAuthoringDocument,
  LoomPrimitiveDefinition,
  LoomSceneNode,
  LoomTransform,
} from "./loomAuthoringModel";
import { compileLoomImplicitField, type LoomAabb } from "./loomImplicitField";

export const LOOM_GPU_UNION_BACKEND = "gpu-explicit-fragment-classification" as const;
export const LOOM_GPU_UNION_FRAGMENT_POLICY = "discard-inside-other-implicit-fields" as const;

export interface LoomGpuUnionOperandPlan {
  boundsMaxUniformName: string;
  boundsMinUniformName: string;
  fieldFunctionName: string;
  fieldSource: string;
  operandNodeId: string;
  primitiveNodeId: string;
}

export interface LoomGpuUnionUniform {
  name: string;
  type: "float" | "vec3";
  value: number | THREE.Vector3;
}

export type LoomGpuImplicitUnionPlan = {
  backend: "none";
  documentRevision: number;
  kind: "unavailable";
  reason: string;
} | {
  backend: typeof LOOM_GPU_UNION_BACKEND;
  documentRevision: number;
  fragmentPolicy: typeof LOOM_GPU_UNION_FRAGMENT_POLICY;
  kind: "available";
  operandIds: readonly string[];
  operands: readonly LoomGpuUnionOperandPlan[];
  parameterSignature: string;
  planSignature: string;
  programSignature: string;
  rootNodeId: string;
  uniforms: readonly LoomGpuUnionUniform[];
};

export interface LoomGpuUnionApplication {
  appliedMaterialCount: number;
  appliedMeshCount: number;
  unmatchedOperandIds: readonly string[];
}

class UnsupportedGpuPredicate extends Error {}

const hash = (value: string): string => {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return (result >>> 0).toString(16).padStart(8, "0");
};

class UniformBuilder {
  readonly values: LoomGpuUnionUniform[] = [];

  float(value: number): string {
    if (!Number.isFinite(value)) throw new UnsupportedGpuPredicate("implicit predicate contains a non-finite parameter");
    const name = `loomGpuUnionU${this.values.length}`;
    this.values.push({ name, type: "float", value });
    return name;
  }

  vec3(values: readonly number[]): string {
    if (values.length !== 3 || values.some((value) => !Number.isFinite(value))) {
      throw new UnsupportedGpuPredicate("implicit predicate contains an invalid vector parameter");
    }
    const name = `loomGpuUnionU${this.values.length}`;
    this.values.push({ name, type: "vec3", value: new THREE.Vector3(values[0], values[1], values[2]) });
    return name;
  }

  declarations(): string {
    return this.values.map(({ name, type }) => `uniform ${type} ${name};`).join("\n");
  }

  parameterSignature(revision: number): string {
    const values = this.values.map((uniform) => uniform.value instanceof THREE.Vector3
      ? uniform.value.toArray() : uniform.value);
    return `gpu-union-parameters-v1:${revision}:${hash(JSON.stringify(values))}`;
  }
}

function inverseTransformSource(operation: LoomTransform, uniforms: UniformBuilder): string {
  if (operation.transform === "translate") return `q -= ${uniforms.vec3(operation.offsetM)};`;
  if (operation.transform === "uniform-scale") return `q /= ${uniforms.float(operation.factor)};`;
  if (operation.transform === "mirror-y") return "q.y = -q.y;";
  if (operation.transform === "mirror") {
    return `q.${operation.axis} = 2.0*${uniforms.float(operation.planeOffsetM)} - q.${operation.axis};`;
  }
  const radians = operation.angleDeg * Math.PI / 180;
  const angle = uniforms.float(radians);
  const origin = uniforms.vec3(operation.originM);
  const rotate = operation.axis === "x"
    ? `q.yz = mat2(cos(${angle}),-sin(${angle}),sin(${angle}),cos(${angle})) * q.yz;`
    : operation.axis === "y"
      ? `q.xz = mat2(cos(${angle}),sin(${angle}),-sin(${angle}),cos(${angle})) * q.xz;`
      : `q.xy = mat2(cos(${angle}),-sin(${angle}),sin(${angle}),cos(${angle})) * q.xy;`;
  return `q -= ${origin}; ${rotate} q += ${origin};`;
}

function nacaFieldSource(name: string, airfoil: LoomAirfoil, uniforms: UniformBuilder): string {
  if (airfoil.family !== "naca4") {
    throw new UnsupportedGpuPredicate(`GPU union does not yet compile ${airfoil.family} airfoil predicates`);
  }
  const thicknessRatio = uniforms.float(airfoil.thicknessRatio);
  const trailingEdge = uniforms.float(airfoil.trailingEdgeThicknessRatio);
  const closed = uniforms.float(airfoil.closedTrailingEdge ? 1 : 0);
  const camberRatio = uniforms.float(airfoil.camberRatio);
  const camberLocation = uniforms.float(airfoil.camberLocationRatio);
  return `float ${name}(float u,float v){float b=clamp(u,0.0,1.0);float k=mix(-0.1015,-0.1036,${closed});float t=5.0*${thicknessRatio}*(0.2969*sqrt(b)-0.126*b-0.3516*b*b+0.2843*b*b*b+k*b*b*b*b)+0.5*${trailingEdge}*b;float p=clamp(${camberLocation},1e-6,0.999999);float m=${camberRatio};float c=b<p?m/(p*p)*(2.0*p*b-b*b):m/((1.0-p)*(1.0-p))*(1.0-2.0*p+2.0*p*b-b*b);return max(max(-u,u-1.0),abs(v-c)-max(0.0,t));}`;
}

function wingFieldSource(
  name: string,
  definition: Extract<LoomPrimitiveDefinition, { primitive: "wing" }>,
  uniforms: UniformBuilder,
): string {
  if (!definition.panels.length) throw new UnsupportedGpuPredicate("GPU wing predicate needs at least one panel");
  const helpers: string[] = [];
  const panels = definition.panels.map((panel, index) => {
    const span = panel.yTipM - panel.yRootM;
    if (Math.abs(span) < 1e-12) throw new UnsupportedGpuPredicate(`GPU wing panel ${panel.panelId} has zero span`);
    const root = `${name}Airfoil${index}Root`;
    const tip = `${name}Airfoil${index}Tip`;
    helpers.push(nacaFieldSource(root, panel.airfoilRoot, uniforms), nacaFieldSource(tip, panel.airfoilTip, uniforms));
    const isRoundTip = index === definition.panels.length - 1 && definition.tip === "round";
    const combine = isRoundTip
      ? `float k=max(${uniforms.float(definition.tipRadiusM)},1e-8);float h=clamp(0.5+0.5*(airfoil-spanGate)/k,0.0,1.0);panelField=mix(spanGate,airfoil,h)+k*h*(1.0-h);`
      : "panelField=max(airfoil,spanGate);";
    const yRoot = uniforms.float(panel.yRootM); const yTip = uniforms.float(panel.yTipM);
    const chordRoot = uniforms.float(panel.chordRootM); const chordTip = uniforms.float(panel.chordTipM);
    const xOffset = uniforms.float(panel.xOffsetM); const sweepLocation = uniforms.float(panel.sweepLocationRatio);
    const sweep = uniforms.float(panel.sweepDeg * Math.PI / 180); const zOffset = uniforms.float(panel.zOffsetM);
    const dihedral = uniforms.float(panel.dihedralDeg * Math.PI / 180);
    const twistRoot = uniforms.float(panel.twistRootDeg * Math.PI / 180); const twistTip = uniforms.float(panel.twistTipDeg * Math.PI / 180);
    const twistLocation = uniforms.float(panel.twistLocationRatio);
    return `{float span=${yTip}-${yRoot};float t=clamp((q.y-${yRoot})/span,0.0,1.0);float chord=mix(${chordRoot},${chordTip},t);float sweepRoot=${xOffset}+${sweepLocation}*${chordRoot};float leading=sweepRoot+tan(${sweep})*span*t-${sweepLocation}*chord;float centerZ=${zOffset}+tan(${dihedral})*span*t;float twist=mix(${twistRoot},${twistTip},t);float dx=q.x-(leading+${twistLocation}*chord);float dz=q.z-centerZ;float u=(cos(twist)*dx-sin(twist)*dz)/chord+${twistLocation};float v=(sin(twist)*dx+cos(twist)*dz)/chord;float airfoil=mix(${root}(u,v),${tip}(u,v),t)*chord;float spanGate=max(min(${yRoot},${yTip})-q.y,q.y-max(${yRoot},${yTip}));float panelField;${combine} result=min(result,panelField);}`;
  });
  return `${helpers.join("\n")}\nfloat ${name}(vec3 p){vec3 q=p;__LOOM_TRANSFORMS__ float result=1e20;${panels.join("\n")}return result;}`;
}

function fuselageFieldSource(
  name: string,
  definition: Extract<LoomPrimitiveDefinition, { primitive: "fuselage" }>,
  uniforms: UniformBuilder,
): string {
  const stations = [...definition.stations].sort((left, right) => left.xM - right.xM);
  if (stations.length < 2) throw new UnsupportedGpuPredicate("GPU fuselage predicate needs at least two stations");
  const compiled = stations.map((station) => {
    const width = station.kind === "point" ? 0 : station.widthM;
    const height = station.kind === "point" ? 0 : station.heightM;
    const wx = station.kind === "point" ? 2 : station.widthExponent;
    const hx = station.kind === "point" ? 2 : station.heightExponent;
    return { x: uniforms.float(station.xM), width: uniforms.float(width), height: uniforms.float(height),
      z: uniforms.float(station.zCenterM), wx: uniforms.float(wx), hx: uniforms.float(hx) };
  });
  const assign = (prefix: "lo" | "hi", station: typeof compiled[number]): string => {
    return `${prefix}X=${station.x};${prefix}W=${station.width};${prefix}H=${station.height};${prefix}Z=${station.z};${prefix}WE=${station.wx};${prefix}HE=${station.hx};`;
  };
  const intervals = compiled.slice(0, -1).map((station, index) => {
    const next = compiled[index + 1]!;
    return `if(q.x>=${station.x}&&q.x<=${next.x}){${assign("lo", station)}${assign("hi", next)}}`;
  }).join("else ");
  const last = compiled[compiled.length - 1]!;
  return `float ${name}(vec3 p){vec3 q=p;__LOOM_TRANSFORMS__ float loX,loW,loH,loZ,loWE,loHE,hiX,hiW,hiH,hiZ,hiWE,hiHE;${assign("lo", compiled[0]!)}${assign("hi", last)}${intervals}float t=clamp((q.x-loX)/max(1e-12,hiX-loX),0.0,1.0);float w=mix(loW,hiW,t)*0.5;float h=mix(loH,hiH,t)*0.5;float z=mix(loZ,hiZ,t);float we=mix(loWE,hiWE,t);float he=mix(loHE,hiHE,t);float radial=(w<=1e-12||h<=1e-12)?length(vec2(q.y,q.z-z)):(pow(abs(q.y/w),we)+pow(abs((q.z-z)/h),he)-1.0)*min(w,h);return max(radial,max(${compiled[0]!.x}-q.x,q.x-${last.x}));}`;
}

function primitiveFieldSource(name: string, definition: LoomPrimitiveDefinition, uniforms: UniformBuilder): string {
  if (definition.primitive === "wing") return wingFieldSource(name, definition, uniforms);
  if (definition.primitive === "fuselage") return fuselageFieldSource(name, definition, uniforms);
  const prefix = `float ${name}(vec3 p){vec3 q=p;__LOOM_TRANSFORMS__ `;
  if (definition.primitive === "sphere") return `${prefix}return length(q)-${uniforms.float(definition.radiusM)};}`;
  if (definition.primitive === "box") return `${prefix}vec3 d=abs(q)-0.5*${uniforms.vec3(definition.dimensionsM)};return length(max(d,vec3(0.0)))+min(max(d.x,max(d.y,d.z)),0.0);}`;
  if (definition.primitive === "ellipsoid") return `${prefix}vec3 r=${uniforms.vec3(definition.radiiM)};return (length(q/r)-1.0)*min(r.x,min(r.y,r.z));}`;
  if (definition.primitive === "pod") {
    const radius = definition.lengthM / (2 * definition.finenessRatio);
    const length = uniforms.float(definition.lengthM); const nose = uniforms.float(definition.noseXM);
    const radiusUniform = uniforms.float(radius);
    if (definition.profile === "ellipse") return `${prefix}return (length(vec3((q.x-(${nose}+0.5*${length}))/(0.5*${length}),q.y/${radiusUniform},q.z/${radiusUniform}))-1.0)*${radiusUniform};}`;
    return `${prefix}float cx=clamp(q.x,${nose}+${radiusUniform},${nose}+${length}-${radiusUniform});return length(vec3(q.x-cx,q.y,q.z))-${radiusUniform};}`;
  }
  throw new UnsupportedGpuPredicate(`GPU union does not yet compile ${definition.primitive} predicates`);
}

function compileOperand(
  operandNodeId: string,
  index: number,
  nodes: ReadonlyMap<string, LoomSceneNode>,
  uniforms: UniformBuilder,
  bounds: LoomAabb,
): LoomGpuUnionOperandPlan {
  const boundsMinUniformName = uniforms.vec3(bounds.min);
  const boundsMaxUniformName = uniforms.vec3(bounds.max);
  const transforms: LoomTransform[] = [];
  let node = nodes.get(operandNodeId);
  const visited = new Set<string>();
  while (node?.type === "transform") {
    if (visited.has(node.id)) throw new UnsupportedGpuPredicate(`GPU union transform cycle at ${node.id}`);
    visited.add(node.id);
    transforms.push(node.operation);
    node = nodes.get(node.inputNodeId);
  }
  if (!node) throw new UnsupportedGpuPredicate(`GPU union operand ${operandNodeId} is missing`);
  if (node.type !== "primitive") throw new UnsupportedGpuPredicate(`GPU union operand ${operandNodeId} must resolve to one primitive`);
  const fieldFunctionName = `loomGpuUnionField${index}`;
  const transformSource = transforms.map((operation) => inverseTransformSource(operation, uniforms)).join(" ");
  const fieldSource = primitiveFieldSource(fieldFunctionName, node.definition, uniforms)
    .replace("__LOOM_TRANSFORMS__", transformSource);
  return {
    boundsMaxUniformName,
    boundsMinUniformName,
    fieldFunctionName,
    fieldSource,
    operandNodeId,
    primitiveNodeId: node.id,
  };
}

export function deriveLoomGpuUnionPlan(document: LoomAuthoringDocument): LoomGpuImplicitUnionPlan {
  const unavailable = (reason: string): LoomGpuImplicitUnionPlan => ({
    backend: "none", documentRevision: document.revision, kind: "unavailable", reason,
  });
  if (document.rootNodeIds.length !== 1) return unavailable("GPU union needs exactly one authored root");
  const nodes = new Map(document.nodes.map((node) => [node.id, node]));
  const root = nodes.get(document.rootNodeIds[0]!);
  if (!root || root.type !== "boolean" || root.operation !== "union") {
    return unavailable("GPU preview supports one top-level hard union only");
  }
  if (root.inputNodeIds.length < 2 || root.inputNodeIds.length > 8) {
    return unavailable("GPU union supports between two and eight explicit operands");
  }
  if (new Set(root.inputNodeIds).size !== root.inputNodeIds.length) {
    return unavailable("GPU union operands must have distinct graph identities");
  }
  try {
    const uniforms = new UniformBuilder();
    const operands = root.inputNodeIds.map((id, index) => compileOperand(
      id,
      index,
      nodes,
      uniforms,
      compileLoomImplicitField({ ...document, rootNodeIds: [id] }).bounds,
    ));
    const source = `${uniforms.declarations()}\n${operands.map((operand) => operand.fieldSource).join("\n")}`;
    const programSignature = `gpu-union-program-v1:${hash(source)}`;
    return {
      backend: LOOM_GPU_UNION_BACKEND,
      documentRevision: document.revision,
      fragmentPolicy: LOOM_GPU_UNION_FRAGMENT_POLICY,
      kind: "available",
      operandIds: operands.map((operand) => operand.operandNodeId),
      operands,
      parameterSignature: uniforms.parameterSignature(document.revision),
      planSignature: programSignature,
      programSignature,
      rootNodeId: root.id,
      uniforms: uniforms.values,
    };
  } catch (error) {
    return unavailable(error instanceof Error ? error.message : "GPU implicit predicate compilation failed");
  }
}

function installFragmentClassifier(
  material: THREE.Material,
  operand: LoomGpuUnionOperandPlan,
  plan: Extract<LoomGpuImplicitUnionPlan, { kind: "available" }>,
): void {
  const others = plan.operands.filter((candidate) => candidate.operandNodeId !== operand.operandNodeId);
  const source = others.map((candidate) => candidate.fieldSource).join("\n");
  const predicate = others.map((candidate) =>
    `loomGpuUnionInsideBounds(vLoomUnionWorldPosition,${candidate.boundsMinUniformName},${candidate.boundsMaxUniformName})&&${candidate.fieldFunctionName}(vLoomUnionWorldPosition)<-1e-5`
  ).join("||");
  const previousCompile = material.onBeforeCompile.bind(material);
  const previousCacheKey = material.customProgramCacheKey.bind(material);
  material.onBeforeCompile = (shader, renderer) => {
    previousCompile(shader, renderer);
    plan.uniforms.forEach((uniform) => { shader.uniforms[uniform.name] = { value: uniform.value }; });
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vLoomUnionWorldPosition;")
      .replace("#include <project_vertex>", "#include <project_vertex>\nvLoomUnionWorldPosition=(modelMatrix*vec4(transformed,1.0)).xyz;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\nvarying vec3 vLoomUnionWorldPosition;\n${plan.uniforms.map(({ name, type }) => `uniform ${type} ${name};`).join("\n")}\nbool loomGpuUnionInsideBounds(vec3 p,vec3 lo,vec3 hi){return all(greaterThanEqual(p,lo))&&all(lessThanEqual(p,hi));}\n${source}`)
      .replace("#include <clipping_planes_fragment>", `#include <clipping_planes_fragment>\nif(${predicate}) discard;`);
  };
  material.customProgramCacheKey = () => `${previousCacheKey()}:${plan.programSignature}:${operand.fieldFunctionName}`;
  material.userData = { ...material.userData, loomGpuUnionParameterSignature: plan.parameterSignature,
    loomGpuUnionPlanSignature: plan.planSignature };
  material.needsUpdate = true;
}

export function applyLoomGpuImplicitUnion(
  scene: THREE.Group,
  plan: Extract<LoomGpuImplicitUnionPlan, { kind: "available" }>,
): LoomGpuUnionApplication {
  const byId = new Map(plan.operands.map((operand) => [operand.operandNodeId, operand]));
  const matched = new Set<string>();
  const roots: { mesh: THREE.Mesh; operand: LoomGpuUnionOperandPlan }[] = [];
  scene.traverse((object) => {
    const mesh = object as THREE.Mesh;
    const operand = typeof mesh.userData.loomEditableNodeId === "string"
      ? byId.get(mesh.userData.loomEditableNodeId) : undefined;
    if (!mesh.isMesh || !operand) return;
    matched.add(operand.operandNodeId);
    roots.push({ mesh, operand });
  });
  const unmatchedOperandIds = plan.operandIds.filter((id) => !matched.has(id));
  if (unmatchedOperandIds.length) {
    return { appliedMaterialCount: 0, appliedMeshCount: 0, unmatchedOperandIds };
  }
  const meshes = new Set<THREE.Mesh>();
  const materials = new Set<THREE.Material>();
  roots.forEach(({ mesh, operand }) => {
    mesh.traverse((candidate) => {
      const rendered = candidate as THREE.Mesh;
      if (!rendered.isMesh || meshes.has(rendered)) return;
      meshes.add(rendered);
      rendered.userData.loomGpuUnionOperandId = operand.operandNodeId;
      const items = Array.isArray(rendered.material) ? rendered.material : [rendered.material];
      items.forEach((material) => {
        if (materials.has(material)) return;
        materials.add(material);
        installFragmentClassifier(material, operand, plan);
      });
    });
  });
  return {
    appliedMaterialCount: materials.size,
    appliedMeshCount: meshes.size,
    unmatchedOperandIds,
  };
}
